import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Copy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiErr } from "@/lib/api";

export default function RequestFuel() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {order_number, share_token}
  const [form, setForm] = useState({
    company_name: "", contact_name: "", phone: "", email: "",
    delivery_address: "", fuel_type: params.get("fuel") || "diesel",
    litres: "", requested_date: "", po_reference: "", special_instructions: "",
  });

  useEffect(() => {
    api.get("/public/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.phone || !form.email) {
      toast.error("Company name, phone number and email are required.");
      return;
    }
    if (!form.litres || Number(form.litres) <= 0) {
      toast.error("Please enter the number of litres.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/quote-request", { ...form, litres: Number(form.litres) });
      setDone({ order_number: data.order_number, share_token: data.share_token });
      toast.success("Request received! We'll prepare your quotation.");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const link = `${window.location.origin}/q/${done.share_token}`;
    return (
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-20">
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-8 text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-[#16A34A]" />
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Request Received</h1>
          <p className="mt-2 text-slate-600">
            Your order number is <span className="font-semibold text-slate-900" data-testid="confirm-order-number">{done.order_number}</span>.
            Our team will confirm your price and email your quotation shortly.
          </p>
          <div className="mt-6 rounded-md bg-[#F9FAFB] border border-[#E5E7EB] p-4 text-left">
            <p className="text-xs uppercase tracking-widest text-slate-400">Your secure quotation link</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 truncate text-sm text-[#0A4F35]" data-testid="confirm-share-link">{link}</code>
              <Button size="sm" variant="outline" data-testid="copy-link-btn"
                onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }}>
                <Copy size={16} />
              </Button>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/q/${done.share_token}`}>
              <Button className="w-full bg-[#0A4F35] hover:bg-[#083D29] text-white">View Quotation</Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="w-full border-[#0A4F35] text-[#0A4F35]">Activate Portal</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-14">
      <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Request Fuel Delivery</h1>
      <p className="mt-2 text-slate-600">No account needed — just tell us what you need. We'll send your quotation.</p>

      <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="request-fuel-form">
        <div className="sm:col-span-2">
          <Label>Company / Organisation name *</Label>
          <Input data-testid="rf-company" value={form.company_name} onChange={set("company_name")} placeholder="e.g. First Bank Plc" className="mt-1.5" />
        </div>
        <div>
          <Label>Contact person</Label>
          <Input data-testid="rf-contact" value={form.contact_name} onChange={set("contact_name")} placeholder="Full name" className="mt-1.5" />
        </div>
        <div>
          <Label>Phone number *</Label>
          <Input data-testid="rf-phone" value={form.phone} onChange={set("phone")} placeholder="+234…" className="mt-1.5" />
        </div>
        <div>
          <Label>Email address *</Label>
          <Input data-testid="rf-email" type="email" value={form.email} onChange={set("email")} placeholder="procurement@company.com" className="mt-1.5" />
        </div>
        <div>
          <Label>Fuel type *</Label>
          <Select value={form.fuel_type} onValueChange={(v) => setForm((f) => ({ ...f, fuel_type: v }))}>
            <SelectTrigger data-testid="rf-fuel-type" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Litres requested *</Label>
          <Input data-testid="rf-litres" type="number" value={form.litres} onChange={set("litres")} placeholder="e.g. 5000" className="mt-1.5" />
        </div>
        <div>
          <Label>Requested delivery date</Label>
          <Input data-testid="rf-date" type="date" value={form.requested_date} onChange={set("requested_date")} className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label>Delivery address</Label>
          <Input data-testid="rf-address" value={form.delivery_address} onChange={set("delivery_address")} placeholder="Site / delivery location" className="mt-1.5" />
        </div>
        <div>
          <Label>PO / Reference number</Label>
          <Input data-testid="rf-po" value={form.po_reference} onChange={set("po_reference")} placeholder="Optional" className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Label>Special instructions</Label>
          <Textarea data-testid="rf-notes" value={form.special_instructions} onChange={set("special_instructions")} placeholder="Anything we should know?" className="mt-1.5" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={submitting} data-testid="rf-submit"
            className="w-full h-12 bg-[#E5B800] hover:bg-[#CCA300] text-black font-bold text-base">
            {submitting ? "Submitting…" : "Submit Request"}
          </Button>
        </div>
      </form>
    </div>
  );
}
